import styled, { keyframes } from "styled-components";
import { TRAFFIC_LIGHT, type TrafficLightStatus } from "@dd-report-components";

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 400px;
  width: 100%;
  color: #6B7280;
  font-size: 0.9375rem;

  svg {
    animation: ${spin} 1s linear infinite;
  }
`;

export const CaseDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  width: 100%;
  padding: 1.5rem 0 4rem;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: ${(props) => props.theme.spacing.small} 0 80px;
  }
`;

export const CaseDetailsHeader = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6B7280;
  font-weight: 500;
  margin-bottom: 1.5rem;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #111827;
  }

  svg {
    font-size: 1.125rem;
  }
`;

export const CaseDetailsContent = styled.div`
  width: 100%;
  max-width: 1400px;
  background-color: #FFFFFF;
  border-radius: 0.75rem;
  padding: 3rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid #E5E7EB;
  min-height: 800px;

  @media (max-width: 1100px) {
    padding: 1.5rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: ${(props) => props.theme.spacing.medium};
  }
`;

export const CaseDetailsTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: ${(props) => props.theme.spacing.medium};
  letter-spacing: -0.025em;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    font-size: ${(props) => props.theme.fontSizes.xlarge};
  }
`;

export const CaseInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2.5rem 4rem;
  margin-top: ${(props) => props.theme.spacing.xlarge};
  margin-bottom: 3rem;
  max-width: 1280px;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    margin-top: ${(props) => props.theme.spacing.large};
    gap: ${(props) => props.theme.spacing.medium};
  }
`;

export const InfoField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

export const InfoLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: #9CA3AF;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const InfoValue = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
`;

export const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  margin-top: 3rem;
  margin-bottom: 1.5rem;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    margin-top: ${(props) => props.theme.spacing.large};
    font-size: ${(props) => props.theme.fontSizes.large};
  }
`;

export const FinalStatementSection = styled.div`
  margin-top: ${(props) => props.theme.spacing.xlarge};
  padding: ${(props) => props.theme.spacing.large};
  background-color: ${(props) => props.theme.colors.secondary};
  border-radius: ${(props) => props.theme.box.borderRadius};
  border-left: 4px solid ${(props) => props.theme.colors.border};

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    margin-top: ${(props) => props.theme.spacing.large};
    padding: ${(props) => props.theme.spacing.medium};
  }
`;

export const FinalStatementLabel = styled.div`
  font-size: ${(props) => props.theme.fontSizes.small};
  font-weight: 600;
  color: ${(props) => props.theme.colors.font.tertiary};
  margin-bottom: ${(props) => props.theme.spacing.small};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const FinalStatementText = styled.p`
  font-size: ${(props) => props.theme.fontSizes.medium};
  color: ${(props) => props.theme.colors.font.primary};
  line-height: 1.6;
  white-space: pre-wrap;
`;

export const FinalReportLink = styled.div`
  margin-top: ${(props) => props.theme.spacing.large};
  padding: ${(props) => props.theme.spacing.medium};
  background-color: rgba(99, 102, 241, 0.05);
  border-radius: ${(props) => props.theme.box.borderRadius};
  border: 1px solid ${(props) => props.theme.colors.border};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.medium};

  &:hover {
    background-color: rgba(99, 102, 241, 0.1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
    border-color: ${(props) => props.theme.colors.accent1};
  }

  svg {
    font-size: ${(props) => props.theme.fontSizes.large};
    color: ${(props) => props.theme.colors.accent1};
    flex-shrink: 0;
  }
`;

export const FinalReportInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const FinalReportName = styled.div`
  font-size: ${(props) => props.theme.fontSizes.medium};
  font-weight: 500;
  color: ${(props) => props.theme.colors.font.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const FinalReportMeta = styled.div`
  font-size: ${(props) => props.theme.fontSizes.small};
  color: ${(props) => props.theme.colors.font.tertiary};
  margin-top: ${(props) => props.theme.spacing.xsmall};
`;

export const DocumentsSection = styled.div`
  margin-top: ${(props) => props.theme.spacing.xlarge};

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    margin-top: ${(props) => props.theme.spacing.large};
  }
`;

export const DocumentsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${(props) => props.theme.spacing.medium};
  margin-top: ${(props) => props.theme.spacing.large};
  width: 100%; // Ensure grid takes full width

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

export const DocumentCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.medium};
  padding: ${(props) => props.theme.spacing.large};
  min-width: 0; // Vital for flex children truncation
  background-color: ${(props) => props.theme.colors.secondary};
  border-radius: ${(props) => props.theme.box.borderRadius};
  border: 1px solid ${(props) => props.theme.colors.border};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: ${(props) => props.theme.spacing.medium};
  }
`;

export const DocumentIcon = styled.div`
  font-size: ${(props) => props.theme.fontSizes.xxlarge};
  color: ${(props) => props.theme.colors.font.tertiary};
  flex-shrink: 0;
`;

export const DocumentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const DocumentName = styled.div`
  font-size: ${(props) => props.theme.fontSizes.medium};
  font-weight: 500;
  color: ${(props) => props.theme.colors.font.primary};
  margin-bottom: ${(props) => props.theme.spacing.small};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DocumentMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing.small};
  font-size: ${(props) => props.theme.fontSizes.small};
  color: ${(props) => props.theme.colors.font.tertiary};
`;

export const DocumentType = styled.span`
  text-transform: capitalize;
`;

export const DocumentSize = styled.span`
  &::before {
    content: "•";
    margin: 0 ${(props) => props.theme.spacing.xsmall};
  }
`;

interface SemaforoIndicatorProps {
  status: TrafficLightStatus | null;
}

export const SemaforoIndicator = styled.div<SemaforoIndicatorProps>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${(props) => {
    switch (props.status) {
      case TRAFFIC_LIGHT.green:
        return '#4caf50';
      case TRAFFIC_LIGHT.yellow:
        return '#ff9800';
      case TRAFFIC_LIGHT.red:
        return '#f44336';
      case TRAFFIC_LIGHT.unknown:
      default:
        return '#9e9e9e';
    }
  }};
  flex-shrink: 0;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: ${(props) => props.theme.spacing.xxlarge};
  color: ${(props) => props.theme.colors.font.tertiary};
  font-size: ${(props) => props.theme.fontSizes.medium};
`;

export const AliasCode = styled.span`
  display: inline-block;
  font-family: 'Courier New', monospace;
  font-size: ${(props) => props.theme.fontSizes.small};
  color: ${(props) => props.theme.colors.font.tertiary};
  font-weight: 500;
  user-select: all;
`;

export const CopyButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.4);
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
    color: rgba(0, 0, 0, 0.6);
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.08);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const CaseIdContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.small};
`;

export const UploadSection = styled.div`
  background-color: #F9FAFB;
  border-radius: 0.5rem;
  padding: 2rem;
  margin-top: ${(props) => props.theme.spacing.large};
  margin-bottom: ${(props) => props.theme.spacing.large};
  border: 2px dashed #E5E7EB;
  scroll-margin-top: 2rem;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: ${(props) => props.theme.spacing.medium};
    margin-left: -16px;
    margin-right: -16px;
    width: calc(100% + 32px);
    border-radius: 0;
  }
`;

export const DropZone = styled.div<{ $isDragging: boolean; $isUploading: boolean }>`
  border: 2px dashed ${(props) => props.$isDragging ? '#A8FBCA' : 'transparent'};
  border-radius: 0.5rem;
  background-color: ${(props) => props.$isDragging ? 'rgba(168, 251, 202, 0.05)' : 'transparent'};
  padding: 2rem 0;
  margin-top: 1rem;
  transition: all 0.2s ease-in-out;
  cursor: ${(props) => props.$isUploading ? 'not-allowed' : 'pointer'};
  position: relative;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not([data-uploading="true"]) {
    border-color: #A8FBCA;
    background-color: rgba(168, 251, 202, 0.03);
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: ${(props) => props.theme.spacing.large};
    min-height: 100px;
  }
`;

export const DropZoneContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  text-align: center;
  color: #111827;
  width: 100%;

  svg {
    color: #9CA3AF;
    transition: transform 0.2s ease-in-out;
    width: 2.5rem;
    height: 2.5rem;
  }

  strong {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #111827;
  }

  span {
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #9CA3AF;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    gap: ${(props) => props.theme.spacing.small};

    svg {
      width: 36px;
      height: 36px;
    }

    strong {
      font-size: 0.875rem;
      line-height: 1.4;
      padding: 0 8px;
    }
  }
`;

export const UploadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1.5rem;
  border-radius: 0.5rem;
  border: none;
  background-color: #000000;
  color: #FFFFFF;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    background-color: #1a1a1a;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    font-size: 1rem;
    width: 1rem;
    height: 1rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    min-height: 44px;
    padding: 12px 24px;
    font-size: 0.875rem;
    width: 100%;
    max-width: 280px;
  }
`;

export const InfoText = styled.p`
  font-size: 0.875rem;
  color: #111827;
  margin-bottom: ${(props) => props.theme.spacing.small};
  margin-top: 0;
  line-height: 1.6;

  &:first-of-type {
    font-weight: 500;
  }

  & + & {
    font-size: 0.75rem;
    color: #6B7280;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    font-size: 0.875rem;
    line-height: 1.5;
    padding: 0 4px;
  }
`;

export const SplitViewLeft = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const SplitViewRight = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

export const SplitViewContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${(props) => props.theme.spacing.large};
  margin-top: ${(props) => props.theme.spacing.medium};
  align-items: start;

  &[data-has-viewer="true"] {
    grid-template-columns: 1fr 1fr;

    ${SplitViewRight} {
      height: 800px;
      min-height: 800px;
    }
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr !important;

    ${SplitViewRight} {
      height: auto;
      min-height: auto;
    }
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

export const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  @keyframes slideIn {
    from { transform: translateY(20px) scale(0.95); opacity: 0; }
    to { transform: translateY(0) scale(1); opacity: 1; }
  }
`;

export const ModalHeader = styled.div`
  margin-bottom: 20px;
`;

export const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
`;

export const ModalBody = styled.div`
  margin-bottom: 24px;
  color: #4B5563;
  font-size: 0.95rem;
  line-height: 1.5;
`;

export const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

export const SecondaryButton = styled.button`
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #D1D5DB;
  background: white;
  color: #374151;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #F3F4F6;
    border-color: #9CA3AF;
  }
`;

export const PrimaryButton = styled.button`
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  background: #A8FBCA;
  color: #111827;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 6px -1px rgba(168, 251, 202, 0.3);

  &:hover:not(:disabled) {
    background: #8FF5C0;
    box-shadow: 0 6px 8px -1px rgba(168, 251, 202, 0.4);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const TierCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

export const TierCardTitle = styled.div`
  font-weight: 700;
  font-size: 1rem;
  color: #111827;
  padding-right: 28px;
  line-height: 1.3;
  min-height: 2.6rem;
  display: flex;
  align-items: flex-start;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    min-height: auto;
  }
`;

export const BrandingSection = styled.div`
  margin-top: ${(props) => props.theme.spacing.xlarge};

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    margin-top: ${(props) => props.theme.spacing.large};
  }
`;

export const BrandingCheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  cursor: pointer;
  font-size: 0.9375rem;
  color: #111827;
  font-weight: 500;

  input[type="checkbox"] {
    width: 1.125rem;
    height: 1.125rem;
    cursor: pointer;
    accent-color: #3B82F6;
  }
`;

export const BrandingPanel = styled.div`
  margin-top: ${(props) => props.theme.spacing.medium};
  padding: 1.5rem;
  background-color: #F9FAFB;
  border-radius: 0.5rem;
  border: 1px solid #E5E7EB;
`;

// ── Report notes (MIKE-329) ──────────────────────────────────────
// Read-only on this page. The notes belong to the report, so this is a way
// back into it, not a second place to write them: two editing surfaces for
// the same rows is where the mental model comes apart.
export const NotesSection = styled.div`
  margin-top: ${(props) => props.theme.spacing.xlarge};
`;

export const NoteEntry = styled.div`
  padding: 0.625rem 0.75rem;
  margin-top: ${(props) => props.theme.spacing.small};
  background-color: rgba(201, 130, 14, 0.05);
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.box.borderRadius};
  /* The peeled corner, same tell the post-it carries inside the report. */
  border-bottom-right-radius: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(201, 130, 14, 0.1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }
`;

// Section on the left, author and date pushed to the far right: the two bits
// of chrome share one line so the card is two lines tall instead of three.
export const NoteEntryHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${(props) => props.theme.spacing.small};
  margin-bottom: 0.1875rem;
`;

export const NoteEntrySection = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #8a6414;
`;

export const NoteEntryText = styled.p`
  margin: 0;
  font-size: ${(props) => props.theme.fontSizes.small};
  color: ${(props) => props.theme.colors.font.primary};
  line-height: 1.45;
  /* Two lines and an ellipsis: this is an index, the note itself is one click
     away in the report where it can be read whole and edited. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const NoteEntryMeta = styled.div`
  flex-shrink: 0;
  font-size: 0.6875rem;
  color: ${(props) => props.theme.colors.font.tertiary};
  white-space: nowrap;
`;
